if (!customElements.get('if-photo-selector')) {
  customElements.define('if-photo-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.scene = this.querySelector('.if-photo-selector__scene');
      this.points = [...this.querySelectorAll('.if-photo-selector__point')];
      this.points.forEach(point => {
        const toggle = point.querySelector('.if-photo-selector__toggle');
        toggle.addEventListener('click', event => {
          if (this.editing || performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); return; }
          this.openPoint === point ? this.close() : this.open(point);
        }, options);
      });
      document.addEventListener('click', event => {
        if (this.openPoint && !this.openPoint.contains(event.target)) this.close();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.openPoint) {
          const toggle = this.openPoint.querySelector('.if-photo-selector__toggle');
          this.close();
          toggle.focus();
        }
      }, options);
      this.closest('.shopify-section')?.addEventListener('shopify:block:select', event => {
        const point = this.points.find(item => item.dataset.blockId === event.detail?.blockId || item === event.target || item.contains(event.target));
        if (point) {
          this.selectedPoint = point;
          this.editing ? this.showPosition(point) : this.open(point);
        }
      }, options);
      this.closest('.shopify-section')?.addEventListener('shopify:block:deselect', () => this.close(), options);
      this.resizeObserver = new ResizeObserver(() => this.positionCard());
      if (this.scene) this.resizeObserver.observe(this.scene);
      window.addEventListener('resize', () => this.positionCard(), options);
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.positionCard();
      });
      if (this.dataset.editor === 'true') this.bindEditor(options);
    }
    bindEditor(options) {
      const button = this.querySelector('[data-edit-points]');
      button?.addEventListener('click', () => {
        this.editing = !this.editing;
        this.close();
        this.classList.toggle('is-editing', this.editing);
        button.setAttribute('aria-pressed', String(this.editing));
        this.querySelector('[data-editor-panel]').hidden = !this.editing;
        if (this.editing) this.showPosition(this.selectedPoint || this.points[0]);
        else this.querySelector('[data-position-result]').hidden = true;
      }, options);
      this.points.forEach(point => {
        const toggle = point.querySelector('.if-photo-selector__toggle');
        toggle.addEventListener('pointerdown', event => {
          if (!this.editing || event.button !== 0) return;
          event.preventDefault();
          this.selectedPoint = point;
          this.drag = { id: event.pointerId, point, toggle };
          toggle.setPointerCapture(event.pointerId);
          this.movePoint(event);
        }, options);
        toggle.addEventListener('pointermove', event => {
          if (this.drag?.id === event.pointerId) this.movePoint(event);
        }, options);
        const finish = event => {
          if (this.drag?.id !== event.pointerId) return;
          this.drag = null;
          this.suppressClickUntil = performance.now() + 400;
          if (toggle.hasPointerCapture(event.pointerId)) toggle.releasePointerCapture(event.pointerId);
        };
        toggle.addEventListener('pointerup', finish, options);
        toggle.addEventListener('pointercancel', finish, options);
        toggle.addEventListener('lostpointercapture', finish, options);
        toggle.addEventListener('keydown', event => {
          if (!this.editing || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          this.selectedPoint = point;
          const mobile = this.mobilePosition(point), suffix = mobile ? '-mobile' : '';
          const css = getComputedStyle(point);
          const x = parseFloat(css.getPropertyValue('--if-x' + suffix)), y = parseFloat(css.getPropertyValue('--if-y' + suffix));
          this.setPosition(point, x + (event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0), y + (event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0));
        }, options);
      });
      this.querySelector('[data-copy-position]')?.addEventListener('click', async () => {
        const field = this.querySelector('[data-position-value]');
        field.focus(); field.select();
        try {
          await navigator.clipboard.writeText(field.value);
          this.querySelector('[data-position-instructions]').textContent = this.dataset.copied;
        } catch {
          this.querySelector('[data-position-instructions]').textContent = this.dataset.savePosition;
        }
      }, options);
    }
    mobilePosition(point) { return matchMedia('(max-width: 749px)').matches && point.dataset.mobilePosition === 'true'; }
    movePoint(event) {
      if (!this.drag || !this.scene) return;
      const rect = this.scene.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.setPosition(this.drag.point, (event.clientX - rect.left) * 100 / rect.width, (event.clientY - rect.top) * 100 / rect.height);
    }
    setPosition(point, x, y) {
      const mobile = this.mobilePosition(point), suffix = mobile ? '-mobile' : '';
      point.style.setProperty('--if-x' + suffix, Math.max(0, Math.min(100, Math.round(x))) + '%');
      point.style.setProperty('--if-y' + suffix, Math.max(0, Math.min(100, Math.round(y))) + '%');
      if (!mobile && point.dataset.mobilePosition !== 'true') {
        point.style.setProperty('--if-x-mobile', Math.max(0, Math.min(100, Math.round(x))) + '%');
        point.style.setProperty('--if-y-mobile', Math.max(0, Math.min(100, Math.round(y))) + '%');
      }
      this.showPosition(point);
    }
    showPosition(point) {
      if (!point) return;
      const panel = this.querySelector('[data-position-result]');
      if (!panel) return;
      panel.hidden = false;
      const mobile = this.mobilePosition(point), suffix = mobile ? '-mobile' : '', css = getComputedStyle(point);
      const field = panel.querySelector('[data-position-value]');
      field.value = parseFloat(css.getPropertyValue('--if-x' + suffix)) + ',' + parseFloat(css.getPropertyValue('--if-y' + suffix));
      panel.querySelector('[data-position-instructions]').textContent = `${this.dataset.pointLabel} ${point.dataset.pointNumber}: ${mobile ? this.dataset.saveMobilePosition : this.dataset.savePosition}`;
      this.points.forEach(item => item.classList.toggle('is-selected', item === point));
    }
    open(point) {
      this.close();
      this.openPoint = point;
      point.classList.add('is-open');
      point.querySelector('.if-photo-selector__toggle').setAttribute('aria-expanded', 'true');
      point.querySelector('.if-photo-selector__card').hidden = false;
      this.positionCard();
    }
    close() {
      if (!this.openPoint) return;
      this.openPoint.classList.remove('is-open');
      this.openPoint.querySelector('.if-photo-selector__toggle').setAttribute('aria-expanded', 'false');
      this.openPoint.querySelector('.if-photo-selector__card').hidden = true;
      this.openPoint = null;
    }
    positionCard() {
      if (!this.openPoint || !this.scene) return;
      const card = this.openPoint.querySelector('.if-photo-selector__card');
      const scene = this.scene.getBoundingClientRect();
      const point = this.openPoint.getBoundingClientRect();
      const gap = 14, inset = 12;
      const minLeft = Math.max(inset, inset - scene.left);
      const maxRight = Math.min(scene.width - inset, document.documentElement.clientWidth - scene.left - inset);
      card.style.setProperty('--if-photo-card-width', Math.min(380, Math.max(0, maxRight - minLeft)) + 'px');
      card.style.setProperty('--if-photo-card-height', Math.max(0, scene.height - inset * 2) + 'px');
      const width = card.offsetWidth, height = card.offsetHeight;
      const x = point.left - scene.left, y = point.top - scene.top;
      const left = Math.max(minLeft, Math.min(x + point.width / 2 - width / 2, maxRight - width));
      let top = y - height - gap;
      if (top < inset) top = y + point.height + gap;
      if (top + height > scene.height - inset) top = Math.max(inset, Math.min(y - height - gap, scene.height - height - inset));
      card.style.left = (left - x) + 'px';
      card.style.top = (top - y) + 'px';
    }
    disconnectedCallback() {
      this.close();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
